import useAsclepius from "@common/composables/api/asclepius"
import { QueryApiCacheKeys } from "@common/config/query-api-cache-keys"
import { useQuery } from "@tanstack/vue-query"
import {
  CompanyMember,
  DisconnectWorkforceIntegrationResponse,
  EMIProviderLinkStatus,
  HrisEmployee,
  ListEmployeesNotFoundInHRISResponse,
  ListEmployeesWeCannotAddFromHrisResponse,
  ListWorkforceIntegrationsResponse,
  ListWorkforceIntegrationSyncOperationEventsResponse,
  RegisterWorkforceIntegrationResponse,
  WorkforceIntegration,
  WorkforceIntegrationApiDisconnectWorkforceIntegrationRequest,
  WorkforceIntegrationApiGetWorkforceIntegrationProviderLinkStatusRequest,
  WorkforceIntegrationApiListEmployeesNotFoundInHrisRequest,
  WorkforceIntegrationApiListEmployeesWeCannotAddFromHrisRequest,
  WorkforceIntegrationApiListWorkforceIntegrationsRequest,
  WorkforceIntegrationApiListWorkforceIntegrationSyncOperationEventsRequest,
  WorkforceIntegrationApiRegisterWorkforceIntegrationRequest,
  WorkforceIntegrationSyncOperationEvent
} from "@vitable/asclepius-api-client-ts"
import { Reactive, reactive, ref, Ref, watch } from "vue"
import { PageData } from "@common/types"
export function useRegisterWorkforceIntegration() {
  const { getWorkforceIntegrationApi, callWithResponse, callResult } = useAsclepius()

  const integration: Ref<Nullable<WorkforceIntegration>> = ref(null)

  async function registerIntegration(request: WorkforceIntegrationApiRegisterWorkforceIntegrationRequest) {
    const data: RegisterWorkforceIntegrationResponse = await callWithResponse(() =>
      getWorkforceIntegrationApi().registerWorkforceIntegration(request)
    )
    integration.value = data.integration
  }

  return {
    registerIntegration,
    registerIntegrationResult: callResult,
    integration
  }
}

export function useListWorkforceIntegrations() {
  const { getWorkforceIntegrationApi, callWithResponse, callResult } = useAsclepius()

  const integrations: Ref<Array<WorkforceIntegration>> = ref([])

  async function listIntegrations(request: WorkforceIntegrationApiListWorkforceIntegrationsRequest) {
    const data: ListWorkforceIntegrationsResponse = await callWithResponse(() =>
      getWorkforceIntegrationApi().listWorkforceIntegrations(request)
    )
    integrations.value = data.results
  }

  return {
    listIntegrations,
    listIntegrationsResult: callResult,
    integrations
  }
}

export function useListSyncOperationEvents() {
  const { getWorkforceIntegrationApi, callWithResponse, callResult } = useAsclepius()

  const syncEventsPaginated = reactive<Paginated<WorkforceIntegrationSyncOperationEvent>>({
    currentPage: 1,
    nextPage: undefined,
    prevPage: undefined,
    totalCount: 0,
    results: []
  })

  async function listSyncEvents(request: WorkforceIntegrationApiListWorkforceIntegrationSyncOperationEventsRequest) {
    request = { ...request, page: syncEventsPaginated.currentPage }

    const data: ListWorkforceIntegrationSyncOperationEventsResponse = await callWithResponse(() =>
      getWorkforceIntegrationApi().listWorkforceIntegrationSyncOperationEvents(request)
    )

    syncEventsPaginated.results = data.results
    syncEventsPaginated.nextPage = data.next
    syncEventsPaginated.prevPage = data.previous
    syncEventsPaginated.totalCount = data.count
  }

  return {
    listSyncEvents,
    listSyncEventsResult: callResult,
    syncEventsPaginated
  }
}

export function useListEmployeesNotFoundInHris(options: WorkforceIntegrationApiListEmployeesNotFoundInHrisRequest) {
  const { getWorkforceIntegrationApi, callWithResponse, callResult: listEmployeesNotFoundInHrisResult } = useAsclepius()

  const request: Reactive<Mutable<WorkforceIntegrationApiListEmployeesNotFoundInHrisRequest>> = reactive({
    companyId: options.companyId,
    page: options?.page ?? 1,
    limit: options?.limit ?? 20
  })

  async function listEmployeesNotFoundInHris(): Promise<PageData<CompanyMember>> {
    const data: ListEmployeesNotFoundInHRISResponse = await callWithResponse(() =>
      getWorkforceIntegrationApi().listEmployeesNotFoundInHris(request)
    )

    return {
      count: data.count,
      results: data.results
    }
  }

  const useListEmployeesNotFoundInHrisQuery = () => {
    const query = useQuery({
      queryKey: [QueryApiCacheKeys.EMPLOYEES_NOT_FOUND_IN_HRIS, request.companyId, request.page, request.limit],
      queryFn: listEmployeesNotFoundInHris
    })

    watch(request, () => {
      query.refetch()
    })

    return query
  }

  return {
    useListEmployeesNotFoundInHrisQuery,
    request,
    listEmployeesNotFoundInHrisResult,
    listEmployeesNotFoundInHris
  }
}

export function useListEmployeesWeCannotAddFromHris(
  options: WorkforceIntegrationApiListEmployeesWeCannotAddFromHrisRequest
) {
  const {
    getWorkforceIntegrationApi,
    callWithResponse,
    callResult: listEmployeesWeCannotFromHrisResult
  } = useAsclepius()

  const request: Reactive<Mutable<WorkforceIntegrationApiListEmployeesWeCannotAddFromHrisRequest>> = reactive({
    companyId: options.companyId,
    page: options?.page ?? 1,
    limit: options?.limit ?? 20
  })

  async function listEmployeesWeCannotAddFromHris(): Promise<PageData<HrisEmployee>> {
    const data: ListEmployeesWeCannotAddFromHrisResponse = await callWithResponse(() =>
      getWorkforceIntegrationApi().listEmployeesWeCannotAddFromHris(request)
    )

    return {
      count: data.count,
      results: data.results
    }
  }

  const useListEmployeesWeCannotAddFromHrisQuery = () => {
    const query = useQuery({
      queryKey: [QueryApiCacheKeys.EMPLOYEES_WE_CANNOT_ADD_FROM_HRIS, request.companyId, request.page, request.limit],
      queryFn: listEmployeesWeCannotAddFromHris
    })

    watch(request, () => {
      query.refetch()
    })

    return query
  }

  return {
    useListEmployeesWeCannotAddFromHrisQuery,
    request,
    listEmployeesWeCannotAddFromHris,
    listEmployeesWeCannotFromHrisResult
  }
}

export function useGetProviderLinkStatus() {
  const { getWorkforceIntegrationApi, callWithResponse, callResult } = useAsclepius()

  const providerLinkStatus: Ref<Nullable<EMIProviderLinkStatus>> = ref(null)

  async function getProviderLinkStatus(
    request: WorkforceIntegrationApiGetWorkforceIntegrationProviderLinkStatusRequest
  ) {
    const data: EMIProviderLinkStatus = await callWithResponse(() =>
      getWorkforceIntegrationApi().getWorkforceIntegrationProviderLinkStatus(request)
    )
    providerLinkStatus.value = data
  }

  return {
    getProviderLinkStatus,
    getProviderLinkStatusResult: callResult,
    providerLinkStatus
  }
}

export function useDisconnectWorkforceIntegration() {
  const { getWorkforceIntegrationApi, callWithResponse, callResult } = useAsclepius()

  const disconnectedIntegration: Ref<Nullable<WorkforceIntegration>> = ref(null)

  async function disconnectIntegration(request: WorkforceIntegrationApiDisconnectWorkforceIntegrationRequest) {
    const data: DisconnectWorkforceIntegrationResponse = await callWithResponse(() =>
      getWorkforceIntegrationApi().disconnectWorkforceIntegration(request)
    )
    disconnectedIntegration.value = data.integration
  }

  return {
    disconnectIntegration,
    disconnectIntegrationResult: callResult,
    disconnectedIntegration
  }
}
